import { Tabs, TabsProps } from 'antd';
import dayjs from 'dayjs';
import React, { FC, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { ReactSVG } from 'react-svg';
import Dropdown from 'src/components/common/Dropdown';
import { formatCurrency, getSkinId } from 'src/helpers/utils';
import { setPrize, transformRafflePrizePool } from 'src/hooks/usePrizeDrawing';
import { getFinishedRafflePrizes, getOnlyExpireTimePassedRaffles } from 'src/services/raffleDrawApi';
import { RootState } from 'src/types/store-types';
import { IActiveRafflesType, IFinishedRafflePrizes, IRaffleDrawTickets } from '../../types';
import './styles.scss';

const RaffleTicketSvg: FC<{ ticket: number }> = memo(({ ticket }) => {
  const beforeInjectionRef = useRef<(svg: SVGSVGElement) => void>(() => undefined);

  beforeInjectionRef.current = (svg: SVGSVGElement) => {
    const textEl = document.createElementNS('http://www.w3.org/2000/svg', 'text');
    textEl.setAttribute('x', '50%');
    textEl.setAttribute('y', '50%');
    textEl.setAttribute('fill', 'var(--text-primary)');
    textEl.setAttribute('font-size', '12');
    textEl.setAttribute('font-weight', '600');
    textEl.setAttribute('text-anchor', 'middle');
    textEl.setAttribute('dominant-baseline', 'middle');
    textEl.textContent = `NO: ${ticket}`;
    svg.appendChild(textEl);
  };

  const handleBeforeInjection = useCallback((svg: SVGSVGElement) => {
    beforeInjectionRef.current(svg);
  }, []);

  return <ReactSVG src="/images/icons/svg/raffleDraw.svg" beforeInjection={handleBeforeInjection} />;
});

const RaffleDrawTickets: FC<IRaffleDrawTickets> = ({ raffleData, prizePool }) => {
  const { t } = useTranslation();
  const skinId = getSkinId();

  const { user } = useSelector((state: RootState) => state.user);

  const [finishedRafflePrizes, setFinishedRafflePrizes] = useState<IFinishedRafflePrizes[]>([]);
  const [finishedRaffleOptions, setFinishedRaffleOptions] = useState<SelectOptionType | null>(null);
  const [onlyExpireTimePassedRaffles, setOnlyExpireTimePassedRaffles] = useState<IActiveRafflesType[] | null>(null);
  const lastFinishedRaffleIdRef = useRef<number | null>(null);
  const lastHeader = prizePool?.[0]?.headers?.at(-1);

  const getFinishedRafflePrizesReq = async (id: number): Promise<void> => {
    const res = await getFinishedRafflePrizes({ raffleId: id });
    if (res.success) setFinishedRafflePrizes(res.result);
    else console.error(res);
  };

  const getOnlyExpireTimePassedRafflesReq = async (): Promise<void> => {
    const res = await getOnlyExpireTimePassedRaffles({ onlyExpireTimePassed: true, operatorId: skinId });
    if (res.success) setOnlyExpireTimePassedRaffles(res.result?.raffles);
    else console.error(res);
  };

  const raffleOptions = useMemo(() => {
    return (
      onlyExpireTimePassedRaffles
        ?.filter((raffle) => raffleData?.category && raffle.category === raffleData.category)
        .map((raffle) => ({
          label: `${raffle.name} - ${raffle.id} / ${dayjs(raffle.start_time).format('D.M.YY')}`,
          value: raffle.id,
        })) ?? []
    );
  }, [onlyExpireTimePassedRaffles, raffleData?.category]);

  useEffect(() => {
    if (!raffleOptions.length) return;

    const preferred = raffleOptions.find((option) => option.value === finishedRaffleOptions?.value) ?? raffleOptions[0];
    if (!preferred) return;

    if (finishedRaffleOptions?.value !== preferred.value) {
      setFinishedRaffleOptions(preferred);
    }

    if (lastFinishedRaffleIdRef.current === preferred.value) return;
    lastFinishedRaffleIdRef.current = preferred.value;
    void getFinishedRafflePrizesReq(Number(preferred.value));
  }, [raffleOptions, finishedRaffleOptions?.value]);

  const renderTickets = (tickets?: number[]): JSX.Element => {
    if (!user) {
      return (
        <div className="raffle_draw_tickets_wrapper">
          <div className="empty-account-result">{t('userTicket')}</div>
        </div>
      );
    }

    if (!tickets?.length) {
      return (
        <div className="raffle_draw_tickets_wrapper">
          <div className="empty_text">{t('noTickets')}</div>
        </div>
      );
    }

    return (
      <div className="raffle_draw_tickets_wrapper">
        <div className="raffle_draw_tickets">
          {tickets.map((el) => (
            <div className="ticket_item" key={el}>
              <RaffleTicketSvg ticket={el} />
            </div>
          ))}
        </div>
      </div>
    );
  };

  const tableData = useMemo(() => transformRafflePrizePool(prizePool?.[0]?.headers), [prizePool]);

  const renderPrizePool = (): JSX.Element => {
    return (
      <div className="raffle_draw_prizePool_tabs">
        {tableData?.map((row) => (
          <div className="raffle_draw_prizePool_tabs_container" key={`${row.place}-${row.type}`}>
            <p className="raffle_draw_prizePool_tabs_container_value">
              <span>{t('place')}</span>
              {row.place}
            </p>
            <p className="raffle_draw_prizePool_tabs_container_value">
              <span>{t('prize')}</span>
              {row.prize}
            </p>
          </div>
        ))}
      </div>
    );
  };

  const handleSelect = (data: SelectOptionType): void => {
    setFinishedRaffleOptions(data);
    if (data.value) {
      void getFinishedRafflePrizesReq(Number(data.value));
    }
  };

  const renderFinishedTickets = (): JSX.Element => {
    return (
      <>
        {raffleOptions?.length > 0 && (
          <Dropdown
            list={raffleOptions}
            selected={finishedRaffleOptions}
            handleSelect={handleSelect}
            listClassName="raffle_draw_finished_tabs_list"
            containerClassName="raffle_draw_finished_tabs_dropdown"
          />
        )}
        <div className="raffle_draw_finished_tabs">
          {finishedRafflePrizes?.length ? (
            finishedRafflePrizes?.map((row) => (
              <div className="raffle_draw_finished_tabs_container" key={row.ticketId}>
                <p className="raffle_draw_finished_tabs_container_value indexOfTab">{row.rank}</p>
                <p className="raffle_draw_finished_tabs_container_value">
                  <span>{t('ticket')}</span>
                  {row.ticketId}
                </p>
                <p className="raffle_draw_finished_tabs_container_value">
                  <span>{t('fullName')}</span>
                  {row.name}
                </p>
                <p className="raffle_draw_finished_tabs_container_value">
                  <span>{t('prize')}</span>
                  {setPrize(row.prize)}
                </p>
              </div>
            ))
          ) : (
            <div className="empty-finished-result">{t('noData')}</div>
          )}
        </div>
      </>
    );
  };

  const items: TabsProps['items'] = [
    {
      key: 'prizePool',
      label: t('prize'),
      children: renderPrizePool(),
    },
    {
      key: 'myTickets',
      label: t('myTickets'),
      children: renderTickets(raffleData?.tickets),
    },
    {
      key: 'finished',
      label: t('finished'),
      children: renderFinishedTickets(),
    },
  ];

  const onChange = (key: string): void => {
    if (key === 'finished') {
      void getOnlyExpireTimePassedRafflesReq();
    }
  };

  return (
    <>
      <div className="raffle_draw_player_tickets_container">
        <div className="tickets_row">
          <span className="tickets_row_value">{raffleData.total_tickets ?? 0}</span>
          <span className="tickets_row_name">{t('totalTickets')}</span>
        </div>
        <div className="tickets_row">
          <span className="tickets_row_value">{raffleData.account_tickets ?? 0}</span>
          <span className="tickets_row_name">{t('myTotalTickets')}</span>
        </div>
        <div className="tickets_row">
          <span className="tickets_row_value">{lastHeader?.place_to ?? 0}</span>
          <span className="tickets_row_name">{t('totalWinTickets')}</span>
        </div>
        <div className="tickets_row">
          <span className="tickets_row_value">{formatCurrency(raffleData.total_prize, raffleData.currency)}</span>
          <span className="tickets_row_name">{t('totalPrizes')}</span>
        </div>
      </div>
      <div className="raffle_draw_container">
        <div className="raffle_draw_container_tabs">
          <Tabs defaultActiveKey="prizePool" items={items} animated onChange={onChange} />
        </div>
      </div>
    </>
  );
};

export default RaffleDrawTickets;
